import { useState, useRef, useEffect, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { DocumentReferenceLink, IntakeData, UploadedMedia } from "@/lib/types";
import { Upload, Loader2, X, FileImage, FileText, CheckCircle, File, Image, Eye, Cloud, Shield, Zap, Camera, Link2, Plus, Info } from "lucide-react";
import { scrollToFirstError } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { patientIntakeApi } from "@/lib/patientIntakeApi";
import { IntakeNavigation } from "./IntakeNavigation";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import ModernCameraCapture from "./ModernCameraCapture";

interface UploadsStepProps { 
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
  onBack: () => void;
}

// Type guard to check if an item is an UploadedMedia
const isUploadedMedia = (item: File | UploadedMedia): item is UploadedMedia => {
  return 'isUploaded' in item && item.isUploaded === true;
};

const createLinkRowId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `link-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const validateDocumentReferenceUrl = (raw: string): boolean => {
  const value = raw.trim();
  if (!value) {
    return false;
  }
  try {
    const parsed = new URL(value);
    return (
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      Boolean(parsed.hostname)
    );
  } catch {
    return false;
  }
};

const isNonEmptyValidDocumentUrl = (raw: string): boolean => {
  const value = raw.trim();
  if (!value) {
    return false;
  }
  return validateDocumentReferenceUrl(value);
};



export default function UploadsStep({
  data,
  onUpdate, 
  onNext,
  onBack,
}: UploadsStepProps) {
  const { i18n } = useLingui();
  const [uploads, setUploads] = useState<{
    labsImaging: (File | UploadedMedia)[];
    photos: (File | UploadedMedia)[];
  }>(() => {
    // Check if data.uploads exists and has actual content
    const hasUploads = data.uploads && 
      (Object.keys(data.uploads).length > 0) &&
      (Array.isArray(data.uploads.labsImaging) || Array.isArray(data.uploads.photos));
    
    console.log('🔧 Initializing uploads:', {
      hasDataUploads: !!data.uploads,
      dataUploadsKeys: data.uploads ? Object.keys(data.uploads) : [],
      hasUploads,
      labsImaging: data.uploads?.labsImaging,
      photos: data.uploads?.photos
    });
    
    if (!hasUploads) {
      console.log('🔧 No uploads found, starting fresh');
      return {
        labsImaging: [],
        photos: [],
      };
    }
    
    return {
      labsImaging: Array.isArray(data.uploads.labsImaging) ? data.uploads.labsImaging : [],
      photos: Array.isArray(data.uploads.photos) ? data.uploads.photos : [],
    };
  });

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [filePreviewUrls, setFilePreviewUrls] = useState<Map<string, string>>(new Map());
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraCategory, setCameraCategory] = useState<keyof typeof uploads | null>(null);
  const documentReferenceLinksSignature = JSON.stringify(
    data.documentReferenceLinks ?? [],
  );
  const [linkRows, setLinkRows] = useState<
    Array<{ id: string; url: string; label: string }>
  >(() => {
    const links = data.documentReferenceLinks;
    if (links?.length) {
      return links.map((link) => ({
        id: createLinkRowId(),
        url: link.url,
        label: link.label ?? "",
      }));
    }
    return [{ id: createLinkRowId(), url: "", label: "" }];
  });
  const isMountedRef = useRef(true);

  // Cleanup on unmount to prevent state updates
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const links = data.documentReferenceLinks;
    if (!links?.length) {
      setLinkRows([{ id: createLinkRowId(), url: "", label: "" }]);
      return;
    }
    setLinkRows(
      links.map((link) => ({
        id: createLinkRowId(),
        url: link.url,
        label: link.label ?? "",
      })),
    );
  }, [documentReferenceLinksSignature]);


  // Clear uploads when procedure changes
  const procedureRef = useRef(data.procedures);
  useEffect(() => {
    console.log('🔍 Procedure check:', {
      previous: procedureRef.current,
      current: data.procedures,
      hasChanged: procedureRef.current !== data.procedures,
      shouldClear: procedureRef.current !== data.procedures && procedureRef.current !== ''
    });
    
    // If procedure has changed, clear uploads and delete from backend
    if (procedureRef.current !== data.procedures && procedureRef.current !== '') {
      console.log('🔄 Procedure changed, clearing uploads and deleting from backend');
      
      // Delete all uploaded media from backend
      const ensureMediaDeleted = async () => {
        const allMedia = [...uploads.labsImaging, ...uploads.photos];
        for (const item of allMedia) {
          if (isUploadedMedia(item)) {
            try {
              console.log(`🗑️ Deleting orphaned media due to procedure change: ${item.id}`);
              await patientIntakeApi.deleteMedia(item.id);
            } catch (error) {
              console.error(`❌ Failed to delete orphaned media ${item.id}:`, error);
            }
          }
        }
      };
      
      // Execute deletion without blocking UI updates
      ensureMediaDeleted();

      setUploads({
        labsImaging: [],
        photos: [],
      });
      setLinkRows([{ id: createLinkRowId(), url: "", label: "" }]);
      onUpdate({ uploads: {}, documentReferenceLinks: [] });
      
      // Also clear preview URLs
      filePreviewUrls.forEach(url => URL.revokeObjectURL(url));
      setFilePreviewUrls(new Map());
    }
    procedureRef.current = data.procedures;
  }, [data.procedures, onUpdate, filePreviewUrls, uploads]);

  // Fetch already-uploaded media when component mounts
  useEffect(() => {
    const fetchUploadedMedia = async () => {
      if (!data.formUid) return;
      
      // Skip if uploads were intentionally cleared (empty object with no keys)
      const uploadsCleared = data.uploads && Object.keys(data.uploads).length === 0;
      if (uploadsCleared) {
        console.log('⏭️ Skipping media fetch - uploads were cleared');
        return;
      }
      
      try {
        setLoadingMedia(true);
        const mediaList = await patientIntakeApi.getFormMedia(data.formUid);
        
        if (!isMountedRef.current) return;
        
        // Group media by category (you may need to adjust based on your API response)
        const mediaByCategory: {
          labsImaging: UploadedMedia[];
          photos: UploadedMedia[];
        } = {
          labsImaging: [],
          photos: [],
        };

        mediaList.forEach((media: any) => {
          const uploadedMedia: UploadedMedia = {
            id: media.id,
            filename: media.originalFilename || media.filename || i18n._(t`Unknown`),
            mimeType: media.mimeType || 'application/octet-stream',
            size: media.fileSize || 0,
            downloadUrl: media.downloadUrl,
            mediaType: media.mediaType || 'photo',
            status: media.status,
            isUploaded: true,
          };

          // Categorize based on metadata or mediaType
          // Strictly filter to ensure we don't pick up Dental photos (which have specific positions)
          // into the general "Additional Photos" bucket.
          const category = media.photoPosition || media.category || 'photos';
          
          if (category === 'labsImaging' || category === 'labs_imaging') {
            mediaByCategory.labsImaging.push(uploadedMedia);
          } else if (category === 'photos') {
            // Only include items explicitly categorized as 'photos' (or default)
            // This excludes specific positions like 'upper_occlusal', 'front', etc.
            mediaByCategory.photos.push(uploadedMedia);
          }
        });

        // Ensure both properties exist even if empty
        const normalizedUploads = {
          labsImaging: mediaByCategory.labsImaging || [],
          photos: mediaByCategory.photos || [],
        };
        setUploads(normalizedUploads);
        onUpdate({ uploads: normalizedUploads });
      } catch (error) {
        console.error('Error fetching uploaded media:', error);
      } finally {
        if (isMountedRef.current) {
          setLoadingMedia(false);
        }
      }
    };

    fetchUploadedMedia();
  }, [data.formUid]);

  const handleFileChange = (category: keyof typeof uploads, files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    const validFiles: File[] = [];
    const newErrors: Record<string, string> = {};

    newFiles.forEach((file) => {
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        newErrors[file.name] = i18n._(t`File size must be less than 50MB`);
        return;
      }

      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
      if (!validTypes.includes(file.type)) {
        newErrors[file.name] = i18n._(t`File must be JPEG, PNG, WebP, or PDF`);
        return;
      }

      validFiles.push(file);
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      scrollToFirstError();
      setTimeout(() => setErrors({}), 5000);
      return;
    }

    setUploads((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), ...validFiles],
    }));
  };

  const handleCameraCapture = (imageData: string) => {
    console.log('📸 Camera capture received, category:', cameraCategory);
    if (!cameraCategory) {
      console.error('❌ No camera category set!');
      return;
    }
    
    try {
      console.log('🔄 Converting base64 to Blob...');
      // Convert base64 to blob
      const byteCharacters = atob(imageData.split(',')[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });
      
      // Create a File-like object from blob (for compatibility)
      const timestamp = Date.now();
      const filename = `camera-photo-${cameraCategory}-${timestamp}.jpg`;
      
      // Add File-like properties to the Blob
      const file = blob as any;
      file.name = filename;
      file.lastModified = timestamp;
      file.lastModifiedDate = new Date(timestamp);
      
      console.log('✅ File created:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });
      
      // Add the captured photo to the appropriate category
      setUploads((prev) => {
        const updated = {
          ...prev,
          [cameraCategory]: [...(prev[cameraCategory] || []), file],
        };
        console.log('📁 Updated uploads:', {
          category: cameraCategory,
          totalFiles: updated[cameraCategory].length,
          files: updated[cameraCategory].map(f => ({
            name: (f as any).name || 'uploaded',
            size: (f as any).size
          }))
        });
        return updated;
      });
      
      console.log('🚪 Closing camera...');
      // Close the camera
      closeCamera();
    } catch (error) {
      console.error('❌ Error converting camera capture:', error);
      alert('Failed to save photo. Please try again.');
    }
  };

  const openCamera = (category: keyof typeof uploads) => {
    setCameraCategory(category);
    setShowCamera(true);
  };

  const closeCamera = () => {
    setShowCamera(false);
    setCameraCategory(null);
  };

  const handleDragOver = (e: React.DragEvent, category: string) => {
    e.preventDefault();
    setDragOver(category);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
  };

  const handleDrop = (e: React.DragEvent, category: keyof typeof uploads) => {
    e.preventDefault();
    setDragOver(null);
    const files = e.dataTransfer.files;
    handleFileChange(category, files);
  };

  const removeFile = async (category: keyof typeof uploads, index: number) => {
    const categoryFiles = uploads[category] || [];
    const itemToRemove = categoryFiles[index];
    
    if (itemToRemove) {
      if (isUploadedMedia(itemToRemove)) {
        // It's an uploaded file, delete from backend
        try {
          console.log(`🗑️ Deleting media from backend: ${itemToRemove.id}`);
          // Optimistically remove from UI first to feel snappy, or show loading?
          // Let's removing from UI first, but we might want to handle error if it fails.
          // For now, simpler is better: fire and forget, or await if we want to be strict.
          // User asked to 'delete', so we should try to delete. 
          
          await patientIntakeApi.deleteMedia(itemToRemove.id);
          console.log(`✅ Media deleted successfully: ${itemToRemove.id}`);
        } catch (error) {
          console.error(`❌ Failed to delete media ${itemToRemove.id}:`, error);
          // Optional: Show a toast here. 
          // For now, allow UI removal even if backend fails to avoid stuck UI state.
        }
      } else {
        // Only revoke URL for File objects (local files)
        const file = itemToRemove as File;
        const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
        const previewUrl = filePreviewUrls.get(fileKey);
        
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setFilePreviewUrls(prev => {
            const newMap = new Map(prev);
            newMap.delete(fileKey);
            return newMap;
          });
        }
      }
    }

    setUploads((prev) => ({
      ...prev,
      [category]: (prev[category] || []).filter((_, i) => i !== index),
    }));
  };

  const handleContinue = async () => {
    if (!isMountedRef.current) return;

    const linkFieldErrors: Record<string, string> = {};
    linkRows.forEach((row) => {
      const trimmedUrl = row.url.trim();
      if (trimmedUrl && !validateDocumentReferenceUrl(trimmedUrl)) {
        linkFieldErrors[`link-${row.id}`] = i18n._(
          t`Enter a valid http(s) URL with a hostname.`,
        );
      }
    });
    if (Object.keys(linkFieldErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...linkFieldErrors }));
      scrollToFirstError();
      return;
    }

    const seenUrls = new Set<string>();
    const sanitizedLinks: DocumentReferenceLink[] = [];
    linkRows.forEach((row) => {
      const trimmedUrl = row.url.trim();
      if (!trimmedUrl) {
        return;
      }
      let dedupeKey: string;
      try {
        dedupeKey = new URL(trimmedUrl).href.toLowerCase();
      } catch {
        dedupeKey = trimmedUrl.toLowerCase();
      }
      if (seenUrls.has(dedupeKey)) {
        return;
      }
      seenUrls.add(dedupeKey);
      const trimmedLabel = row.label.trim();
      if (trimmedLabel.length > 0) {
        sanitizedLinks.push({ url: trimmedUrl, label: trimmedLabel });
      } else {
        sanitizedLinks.push({ url: trimmedUrl });
      }
    });

    setUploading(true);

    try {
      // Upload only new files (File objects) if formUid exists
      if (data.formUid && Object.values(uploads).some(arr => arr.length > 0)) {
        // Separate files by category and upload them
        const categories: Array<keyof typeof uploads> = ['labsImaging', 'photos'];
        const updatedUploads = { ...uploads };

        for (const category of categories) {
          const items = uploads[category] || [];
          const newUploadedMedia: (File | UploadedMedia)[] = [];

          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            
            // Skip already-uploaded media
            if (isUploadedMedia(item)) {
              newUploadedMedia.push(item);
              continue;
            }

            // Upload new File objects
            const file = item as File;
            if (!isMountedRef.current) return;
            
            const fileKey = `${file.name}-${file.size}`;
            setUploadProgress((prev) => ({ ...prev, [fileKey]: 0 }));
            
            try {
              // Get presigned URL
              const presignResponse = await patientIntakeApi.getPhotoUploadUrl(
                data.formUid,
                file.type,
                file.size,
                file.name
              );

              if (!isMountedRef.current) return;
              // Upload to cloud
              setUploadProgress((prev) => ({ ...prev, [fileKey]: 50 }));
              await patientIntakeApi.uploadPhotoToCloud(
                presignResponse.data.uploadUrl,
                file
              );

              if (!isMountedRef.current) return;
              // Register the media with category info
              setUploadProgress((prev) => ({ ...prev, [fileKey]: 75 }));
              const registerResponse = await patientIntakeApi.registerPhoto(
                data.formUid!,
                presignResponse.data.bucketName,
                presignResponse.data.storagePath,
                file.name,
                file.type,
                file.size,
                category // Pass category as photoPosition
              );

              if (!isMountedRef.current) return;
              setUploadProgress((prev) => ({ ...prev, [fileKey]: 100 }));

              // Replace File object with UploadedMedia
              const media = registerResponse.data?.media;
              if (media) {
                const uploadedMedia: UploadedMedia = {
                  id: media.id,
                  filename: file.name,
                  mimeType: file.type,
                  size: file.size,
                  downloadUrl: media.downloadUrl || '',
                  mediaType: media.mediaType || 'photo',
                  status: media.status,
                  isUploaded: true,
                };
                newUploadedMedia.push(uploadedMedia);
              }
            } catch (error) {
              console.error(`Error uploading ${file.name}:`, error);
              if (!isMountedRef.current) return;
              setErrors((prev) => ({
                ...prev,
                [fileKey]: i18n._(t`Upload failed. Please try again.`),
              }));
              // Keep the file in the list for retry
              newUploadedMedia.push(item);
            }
          }

          updatedUploads[category] = newUploadedMedia || [];
        }

        // Ensure both properties exist even if empty
        const normalizedUploads = {
          labsImaging: updatedUploads.labsImaging || [],
          photos: updatedUploads.photos || [],
        };
        setUploads(normalizedUploads);
        onUpdate({
          uploads: normalizedUploads,
          documentReferenceLinks: sanitizedLinks,
        });
      } else {
        // No formUid, just update with current uploads (normalize to ensure both properties exist)
        const normalizedUploads = {
          labsImaging: uploads.labsImaging || [],
          photos: uploads.photos || [],
        };
        onUpdate({
          uploads: normalizedUploads,
          documentReferenceLinks: sanitizedLinks,
        });
      }

      if (!isMountedRef.current) return;
      
      // Reset uploading state before navigation
      setUploading(false);
      
      // Navigate to next step
      window.scrollTo({ top: 0, behavior: "smooth" });
      onNext();
    } catch (error) {
      console.error("Error in handleContinue:", error);
      if (isMountedRef.current) {
        setUploading(false);
      }
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return Image;
    } else if (fileType === "application/pdf") {
      return FileText;
    }
    return File;
  };

  const getFilePreview = (item: File | UploadedMedia): string | null => {
    // If it's already uploaded, use the download URL
    if (isUploadedMedia(item)) {
      return item.mimeType.startsWith("image/") ? item.downloadUrl : null;
    }
    
    // For new files, use the object URL
    const file = item as File;
    if (file.type.startsWith("image/")) {
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
      return filePreviewUrls.get(fileKey) || null;
    }
    return null;
  };

  // Create preview URLs for new File objects only
  useEffect(() => {
    const allItems = [...uploads.labsImaging, ...uploads.photos];
    const newUrls = new Map(filePreviewUrls);
    let hasChanges = false;

    allItems.forEach(item => {
      // Only create URLs for File objects, not UploadedMedia
      if (!isUploadedMedia(item)) {
        const file = item as File;
        if (file.type.startsWith("image/")) {
          const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
          if (!newUrls.has(fileKey)) {
            const url = URL.createObjectURL(file);
            newUrls.set(fileKey, url);
            hasChanges = true;
          }
        }
      }
    });

    if (hasChanges) {
      setFilePreviewUrls(newUrls);
    }
  }, [uploads.labsImaging, uploads.photos]);

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup all URLs on unmount
      filePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const UploadSection = ({
    category,
    title,
    description,
    icon: Icon,
    showCamera = true,
  }: {
    category: keyof typeof uploads;
    title: ReactNode;
    description: ReactNode;
    icon: React.ElementType<{ className?: string }>;
    showCamera?: boolean;
  }) => {
    const isDraggedOver = dragOver === category;
    const categoryFiles = uploads[category] || [];
    const hasFiles = categoryFiles.length > 0;
    const fileCount = categoryFiles.length;

    return (
      <div className="space-y-6">
        {/* Section Header */}
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-[#166B93] via-[#166B93] to-[#145A7A] rounded-2xl flex items-center justify-center shadow-lg shadow-[#166B93]/25 ring-1 ring-white/20">
            <Icon className="h-7 w-7 text-white" />
          </div>
          <div className="flex-1">
            <Label className="text-xl font-bold text-gray-900 font-nunito mb-1 block">
              {title}
            </Label>
            <p className="text-base text-gray-600 font-jakarta leading-relaxed">{description}</p>
            {hasFiles && (
              <div className="mt-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-green-700">
                  {fileCount} {fileCount === 1 ? i18n._(t`file`) : i18n._(t`files`)} <Trans>selected</Trans>
                </span>
              </div> 
            )} 
          </div> 
        </div>

        {/* Enhanced Upload Zone */}
        <div 
          className={`relative border-2 border-dashed rounded-2xl p-8 sm:p-12 transition-all duration-300 group cursor-pointer overflow-hidden ${
            isDraggedOver 
              ? 'border-[#166B93] bg-gradient-to-br from-[#166B93]/10 via-[#166B93]/5 to-[#CEF12F]/10 scale-[1.02] shadow-lg' 
              : 'border-gray-300 hover:border-[#166B93]/60 hover:bg-gradient-to-br hover:from-[#166B93]/5 hover:to-[#CEF12F]/5'
          }`}
          onDragOver={(e) => handleDragOver(e, category)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, category)}
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-gradient-to-br from-[#166B93] to-[#CEF12F]"></div>
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23166B93' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}></div>
          </div>

          <input
            type="file"
            id={category}
            multiple
            accept="image/*,.pdf"
            onChange={(e) => handleFileChange(category, e.target.files)}
            className="hidden"
          />
          
          <label htmlFor={category} className="relative flex flex-col items-center justify-center cursor-pointer">
            {/* Upload Icon with Animation */}
            <div className={`relative w-20 h-20 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 ${
              isDraggedOver 
                ? 'bg-[#166B93] shadow-lg shadow-[#166B93]/30 scale-110' 
                : 'bg-gradient-to-br from-gray-100 to-gray-200 group-hover:from-[#166B93]/10 group-hover:to-[#CEF12F]/10 group-hover:scale-105'
            }`}>
              <Upload className={`h-10 w-10 transition-all duration-300 ${
                isDraggedOver 
                  ? 'text-white animate-bounce' 
                  : 'text-gray-400 group-hover:text-[#166B93] group-hover:scale-110'
              }`} />
              
              {/* Floating particles effect */}
              {isDraggedOver && (
                <>
                  <div className="absolute -top-2 -right-2 w-3 h-3 bg-[#CEF12F] rounded-full animate-ping"></div>
                  <div className="absolute -bottom-2 -left-2 w-2 h-2 bg-[#CEF12F] rounded-full animate-ping animation-delay-150"></div>
                </>
              )}
            </div>

            {/* Upload Text */}
            <div className="text-center space-y-2">
              <p className={`text-lg font-semibold transition-colors duration-300 ${
                isDraggedOver ? 'text-[#166B93]' : 'text-gray-700 group-hover:text-[#166B93]'
              }`}>
                {isDraggedOver ? i18n._(t`Drop files here!`) : i18n._(t`Click to upload or drag and drop`)}
              </p>
              <p className="text-sm text-gray-500 font-jakarta">
                <Trans>PNG, JPG, WebP, or PDF • Max 50MB each</Trans>
              </p>
              
              {/* Camera Button */}
              {showCamera && (
                <div className="pt-4">
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      openCamera(category);
                    }}
                    className="bg-gradient-to-r from-[#166B93] to-[#145A7A] hover:from-[#145A7A] hover:to-[#166B93] text-white shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    <Trans>Take Photo</Trans>
                  </Button>
                </div>
              )}
              
              
              {/* Feature badges */}
              <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-200/50">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Shield className="h-3 w-3" />
                  <span>
                    <Trans>Secure</Trans>
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Zap className="h-3 w-3" />
                  <span>
                    <Trans>Fast Upload</Trans>
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Cloud className="h-3 w-3" />
                  <span>
                    <Trans>Cloud Storage</Trans>
                  </span>
                </div>
              </div>
            </div>
          </label>
        </div>

        {/* Enhanced File Grid */}
        {hasFiles && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-gray-900 font-nunito">
                    {i18n._(t`Uploaded Files`)} ({categoryFiles.length})
                  </h4>
              <div className="flex items-center gap-2">
                {/* Buttons removed as requested */}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {categoryFiles.map((item, index) => {
                const isAlreadyUploaded = isUploadedMedia(item);
                const mimeType = isAlreadyUploaded ? item.mimeType : (item as File).type;
                const FileIcon = getFileIcon(mimeType);
                const preview = getFilePreview(item);
                const filename = isAlreadyUploaded ? item.filename : (item as File).name;
                const fileKey = isAlreadyUploaded 
                  ? item.id 
                  : `${(item as File).name}-${(item as File).size}`;
                const isUploaded = isAlreadyUploaded || uploadProgress[fileKey] === 100;
                const isUploading = !isAlreadyUploaded && uploadProgress[fileKey] !== undefined && uploadProgress[fileKey] < 100;
                const fileSize = (isAlreadyUploaded ? item.size : (item as File).size) || 0;
                const fileSizeMB = (fileSize / 1024 / 1024).toFixed(2);
                
                return (
                  <Card key={index} className="group relative overflow-hidden border-2 hover:border-[#166B93]/50 transition-all duration-300 hover:shadow-xl hover:shadow-[#166B93]/10 hover:-translate-y-1">
                    <CardContent className="p-0">
                      {/* File preview/icon area */}
                      <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                        {preview ? (
                          <div className="relative w-full h-full">
                            <img
                              src={preview}
                              alt={filename}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setPreviewImage(preview)}
                                className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-white/95 hover:bg-white shadow-lg backdrop-blur-sm transform scale-90 group-hover:scale-100"
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                <Trans>Preview</Trans>
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center p-6">
                            <div className="w-16 h-16 bg-gradient-to-br from-[#166B93]/10 to-[#166B93]/20 rounded-xl flex items-center justify-center mb-3">
                              <FileIcon className="h-8 w-8 text-[#166B93]" />
                            </div>
                            <span className="text-xs font-medium text-gray-600 text-center">
                              {mimeType === "application/pdf" ? i18n._(t`PDF Document`) : i18n._(t`File`)}
                            </span>
                          </div>
                        )}
                        
                        {/* Remove button */}
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeFile(category, index)}
                          className="absolute top-3 right-3 h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg transform scale-90 group-hover:scale-100"
                        >
                          <X className="h-4 w-4" />
                        </Button>

                        {/* Upload progress overlay */}
                        {isUploading && (
                          <div className="absolute inset-0 bg-gradient-to-br from-[#166B93]/90 to-[#166B93]/80 flex items-center justify-center backdrop-blur-sm">
                            <div className="text-center text-white">
                              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" />
                              <p className="text-sm font-medium">{uploadProgress[fileKey]}%</p>
                              <div className="w-16 h-1 bg-white/30 rounded-full mt-2 overflow-hidden">
                                <div 
                                  className="h-full bg-[#CEF12F] transition-all duration-300 rounded-full"
                                  style={{ width: `${uploadProgress[fileKey]}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Success indicator */}
                        {isUploaded && (
                          <div className="absolute top-3 left-3">
                            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-full p-1.5 shadow-lg">
                              <CheckCircle className="h-4 w-4 text-white" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Enhanced File info */}
                      <div className="p-4 bg-white">
                        <p className="text-sm font-semibold text-gray-900 truncate mb-2" title={filename}>
                          {filename}
                        </p>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500 font-medium">
                            {fileSizeMB} MB
                          </p>
                          {isUploaded && (
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-xs text-green-600 font-semibold">
                                <Trans>Uploaded</Trans>
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Error message */}
                        {errors[fileKey] && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-xs text-red-600 font-medium">{errors[fileKey]}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const totalFiles = (uploads.labsImaging?.length || 0) + (uploads.photos?.length || 0);
  const validLinkRowsCount = linkRows.filter((row) =>
    isNonEmptyValidDocumentUrl(row.url),
  ).length;

  // Show loading state when fetching media
  if (loadingMedia) {
    return (
      <div className="max-w-7xl mx-auto space-y-10">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-[#166B93]" />
            <p className="text-lg font-medium text-gray-700">
              <Trans>Loading your uploaded files...</Trans>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto space-y-10">

        {/* Enhanced Header */}
        <div className="text-center space-y-6 py-8 sm:py-12">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-2xl mb-2 sm:mb-4">
          <Upload className="h-10 w-10 text-white" />
        </div>

        <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 font-nunito px-2 sm:px-4">
          <Trans>Do you have pictures or Lab Results to upload?</Trans>
        </h1>
        <p className="text-xs sm:text-base text-gray-600 font-jakarta max-w-2xl mx-auto leading-relaxed px-2 sm:px-4">
          <Trans>
            Share your medical documents securely to help us provide the best possible care
          </Trans>
        </p>


        

          {/* Feature highlights */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-gray-100">
              <Shield className="h-4 w-4 text-[#166B93]" />
              <span className="text-sm font-medium text-gray-700">
                <Trans>HIPAA Compliant</Trans>
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-gray-100">
              <Zap className="h-4 w-4 text-[#CEF12F]" />
              <span className="text-sm font-medium text-gray-700">
                <Trans>Instant Upload</Trans>
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-gray-100">
              <Cloud className="h-4 w-4 text-[#166B93]" />
              <span className="text-sm font-medium text-gray-700">
                <Trans>Secure Storage</Trans>
              </span>
            </div>
          </div>
        </div>

        {/* Enhanced Upload Summary */}
        {totalFiles > 0 && (
          <div className="relative overflow-hidden bg-gradient-to-r from-[#166B93]/10 via-white to-[#CEF12F]/10 rounded-2xl p-6 border border-[#166B93]/20 shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-r from-[#166B93]/5 to-[#CEF12F]/5"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#166B93] to-[#145A7A] rounded-xl flex items-center justify-center shadow-lg">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900 font-nunito">
                    {totalFiles} {totalFiles === 1 ? 'file' : 'files'} ready for upload
                  </p>
                  <p className="text-sm text-gray-600 font-jakarta">
                    <Trans>Your documents will be securely uploaded when you continue</Trans>
                  </p>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[...Array(Math.min(totalFiles, 3))].map((_, i) => (
                    <div key={i} className="w-8 h-8 bg-gradient-to-br from-[#166B93] to-[#CEF12F] rounded-full border-2 border-white flex items-center justify-center">
                      <FileText className="h-4 w-4 text-white" />
                    </div>
                  ))}
                  {totalFiles > 3 && (
                    <div className="w-8 h-8 bg-gray-200 rounded-full border-2 border-white flex items-center justify-center">
                      <span className="text-xs font-bold text-gray-600">+{totalFiles - 3}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Main Form */}
        <div className="relative overflow-hidden bg-white md:rounded-3xl md:shadow-2xl md:border md:border-gray-100 p-2 sm:p-6 lg:p-12">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#CEF12F]/10 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#166B93]/10 to-transparent rounded-full translate-y-24 -translate-x-24"></div>
          
          <div className="relative space-y-12 sm:space-y-16">
            <UploadSection
              category="labsImaging"
              title={<Trans>Lab Results &amp; Medical Imaging</Trans>}
              description={<Trans>Share recent lab results, X-rays, MRIs, or other medical imaging to help our specialists</Trans>}
              icon={FileImage}
              showCamera={false}
            />

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center">
                <div className="bg-white px-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-[#166B93] to-[#CEF12F] rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>

            <UploadSection
              category="photos"
              title={<Trans>Additional Photos</Trans>}
              description={<Trans>Upload any additional photos that might help with your consultation or treatment planning</Trans>}
              icon={FileImage}
            />

            <div className="space-y-4 rounded-2xl border border-gray-200 bg-gray-50/60 p-6">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-[#166B93]/10 text-[#166B93]">
                  <Link2 className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold text-gray-900 font-nunito">
                    <Trans>Document links (optional)</Trans>
                  </h3>
                  <p className="text-sm text-gray-600 font-jakarta">
                    <Trans>
                      Add share links to files hosted online (for example Google Drive set to anyone with the link).
                    </Trans>
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-[#166B93]/20 bg-[#166B93]/5 p-4">
                <div className="flex items-start gap-3">
                  <Info className="mt-0.5 h-4 w-4 text-[#166B93]" />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-gray-900 font-jakarta">
                      <Trans>Make sure your links are publicly accessible</Trans>
                    </p>
                    <p className="text-sm text-gray-700 font-jakarta">
                      <Trans>
                        Please set sharing permissions so anyone with the link can view or download the file. Links that require sign-in cannot be reviewed.
                      </Trans>
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {linkRows.map((row) => (
                  <div
                    key={row.id}
                    className="space-y-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                  >
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor={`doc-link-url-${row.id}`}>
                          <Trans>URL</Trans>
                        </Label>
                        <input
                          id={`doc-link-url-${row.id}`}
                          type="url"
                          inputMode="url"
                          autoComplete="url"
                          value={row.url}
                          onChange={(event) => {
                            const value = event.target.value;
                            setLinkRows((prev) =>
                              prev.map((item) =>
                                item.id === row.id ? { ...item, url: value } : item,
                              ),
                            );
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next[`link-${row.id}`];
                              return next;
                            });
                          }}
                          placeholder="https://"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                        {errors[`link-${row.id}`] && (
                          <p className="text-sm text-red-600">{errors[`link-${row.id}`]}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`doc-link-label-${row.id}`}>
                          <Trans>Label (optional)</Trans>
                        </Label>
                        <input
                          id={`doc-link-label-${row.id}`}
                          type="text"
                          value={row.label}
                          onChange={(event) => {
                            const value = event.target.value;
                            setLinkRows((prev) =>
                              prev.map((item) =>
                                item.id === row.id ? { ...item, label: value } : item,
                              ),
                            );
                          }}
                          placeholder={i18n._(t`e.g. MRI report`)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          setLinkRows((prev) => {
                            if (prev.length <= 1) {
                              return [
                                {
                                  id: createLinkRowId(),
                                  url: "",
                                  label: "",
                                },
                              ];
                            }
                            return prev.filter((item) => item.id !== row.id);
                          });
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next[`link-${row.id}`];
                            return next;
                          });
                        }}
                      >
                        <Trans>Remove</Trans>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full border-dashed"
                onClick={() =>
                  setLinkRows((prev) => [
                    ...prev,
                    { id: createLinkRowId(), url: "", label: "" },
                  ])
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                <Trans>Add another link</Trans>
              </Button>
            </div>

            {/* Enhanced Form Actions */}
            <IntakeNavigation
              onBack={onBack}
              onNext={handleContinue}
              isNextLoading={uploading}
              nextLabel={
                  <div className="flex items-center gap-3">
                    <span>
                      {totalFiles > 0
                        ? i18n._(t`Continue with`) +
                          ` ${totalFiles} ${totalFiles === 1 ? i18n._(t`file`) : i18n._(t`files`)}`
                        : validLinkRowsCount > 0
                          ? i18n._(t`Continue`)
                          : i18n._(t`Skip for now`)}
                    </span>
                  </div> 
              }
            />
          </div>
        </div>

        {/* Enhanced Info Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-4 sm:px-0">
          {/* Privacy Card */}
          <Card className="border border-white/40 bg-white/40 backdrop-blur-xl shadow-[0_8px_30px_rgb(22,107,147,0.08)] rounded-[2rem] overflow-hidden relative group hover:shadow-[0_8px_30px_rgb(22,107,147,0.12)] transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-[#166B93]/5 to-transparent pointer-events-none" />
            <CardContent className="p-6 sm:p-8 relative z-10">
              <div className="flex flex-col sm:flex-row items-start gap-5">
                <div className="w-14 h-14 bg-gradient-to-br from-[#166B93] to-[#145A7A] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform duration-300">
                  <Shield className="h-7 w-7 text-white" />
                </div>
                <div className="space-y-3 flex-1">
                  <p className="text-xl font-bold text-gray-900 font-nunito tracking-tight">
                    <Trans>Your Privacy Matters</Trans>
                  </p>
                  <p className="text-sm text-gray-600 font-jakarta leading-relaxed">
                    <Trans>
                      All documents are encrypted end-to-end and stored securely in HIPAA-compliant cloud storage. Only authorized medical professionals can access your files.
                    </Trans>
                  </p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-100/50 text-green-700 text-xs font-semibold rounded-xl">
                      <CheckCircle className="h-3.5 w-3.5" />
                      <Trans>256-bit Encryption</Trans>
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-100/50 text-blue-700 text-xs font-semibold rounded-xl">
                      <Shield className="h-3.5 w-3.5" />
                      <Trans>HIPAA Compliant</Trans>
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Better Care Card */}
          <Card className="border border-white/40 bg-white/40 backdrop-blur-xl shadow-[0_8px_30px_rgb(206,241,47,0.12)] rounded-[2rem] overflow-hidden relative group hover:shadow-[0_8px_30px_rgb(206,241,47,0.2)] transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-[#CEF12F]/10 to-transparent pointer-events-none" />
            <CardContent className="p-6 sm:p-8 relative z-10">
              <div className="flex flex-col sm:flex-row items-start gap-5">
                <div className="w-14 h-14 bg-gradient-to-br from-[#CEF12F] to-[#B8E526] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform duration-300">
                  <Zap className="h-7 w-7 text-[#166B93]" />
                </div>
                <div className="space-y-3 flex-1">
                  <p className="text-xl font-bold text-gray-900 font-nunito tracking-tight">
                    <Trans>Better Care Through Information</Trans>
                  </p>
                  <p className="text-sm text-gray-600 font-jakarta leading-relaxed">
                    <Trans>Providing medical records helps our specialists understand your health history and create more accurate, personalized treatment plans.</Trans>
                  </p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-100/50 text-purple-700 text-xs font-semibold rounded-xl">
                      <FileText className="h-3.5 w-3.5" />
                      <Trans>Max 50MB</Trans>
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-100/50 text-orange-700 text-xs font-semibold rounded-xl">
                      <Image className="h-3.5 w-3.5" />
                      <Trans>Multiple Formats</Trans>
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Enhanced Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-6xl max-h-full animate-fade-in">
            {/* Close button */}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setPreviewImage(null)}
              className="absolute -top-12 right-0 z-10 bg-white/95 hover:bg-white shadow-lg backdrop-blur-sm h-10 w-10 p-0 rounded-full"
            >
              <X className="h-5 w-5" />
            </Button>
            
            {/* Image container */}
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
              <img
                src={previewImage}
                alt={i18n._(t`Document Preview`)}
                className="max-w-full max-h-[80vh] object-contain block"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Image info overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-sm font-medium">
                  <Trans>Document Preview</Trans>
                </p>
                <p className="text-white/80 text-xs">
                  <Trans>Click outside to close</Trans>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modern Camera Capture Modal */}
      {showCamera && cameraCategory && (
        <ModernCameraCapture
          onCapture={handleCameraCapture}
          onClose={closeCamera}
        />
      )}
    </>
  );
}

